import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { Button } from "../../components/Button";
import { apiJson, clearAdminApiState } from "../../lib/adminApi";
import { visibleReviewCredentials } from "../../config/reviewMode";

type Values = { email: string; password: string };

export function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const reduceMotion = useReducedMotion();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<Values>();
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex,nofollow,noarchive"; document.head.append(meta);
    return () => meta.remove();
  }, []);
  const submit = handleSubmit(async (values) => {
    setError("");
    try {
      await apiJson("/api/admin/auth/login", { method: "POST", body: JSON.stringify(values) });
      clearAdminApiState();
      const from = (location.state as { from?: string } | null)?.from ?? "/admin/submissions";
      navigate(from, { replace: true });
    } catch { setError("The email or password is not correct."); }
  });
  return (
    <div className="admin-login-page">
      <m.main className="admin-login-card" initial={reduceMotion ? false : { opacity: 0.78, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}>
        <div className="wordmark">HuddleCo</div>
        <p className="eyebrow">Friction Finder internal</p>
        <h1>Sign in</h1>
        <p className="login-intro">Use your authorised HuddleCo admin details.</p>
        {visibleReviewCredentials && (
          <aside className="review-login-details" aria-label="Local review login details">
            <strong>Seeded review login</strong>
            <span>{visibleReviewCredentials.email}</span>
            <span>{visibleReviewCredentials.password}</span>
          </aside>
        )}
        <AnimatePresence initial={false}>{error && <m.div className="error-summary" role="alert" initial={reduceMotion ? false : { opacity: 0.01, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: reduceMotion ? 0 : 0.16 }}>{error}</m.div>}</AnimatePresence>
        <form onSubmit={submit} noValidate aria-busy={isSubmitting}>
          <div className="field">
            <label htmlFor="admin-email">Work email</label>
            <input id="admin-email" type="email" autoComplete="username" aria-invalid={!!errors.email} {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} />
            {errors.email && <p className="validation-message" role="alert">Enter a valid work email address.</p>}
          </div>
          <div className="field">
            <label htmlFor="admin-password">Password</label>
            <input id="admin-password" type="password" autoComplete="current-password" aria-invalid={!!errors.password} {...register("password", { required: true })} />
            {errors.password && <p className="validation-message" role="alert">Enter your password.</p>}
          </div>
          <Button type="submit" fullWidth disabled={isSubmitting}>{isSubmitting ? "Signing in…" : "Sign in"}</Button>
        </form>
      </m.main>
    </div>
  );
}
